const ROLE_LEVELS = {
  BASE: 'base',
  INSTRUCTOR: 'instructor',
  ADVANCED: 'advanced',
};

const ARS_EXCEPTION_USER_IDS = new Set([91, 5]);
const APP_SUPPORT_EXCEPTION_USER_IDS = new Set([5]);
const DATA_EXCHANGE_READER_ROLE = 'datenaustausch_leser';
const DATA_EXCHANGE_MANAGER_ROLES = new Set(['admin', 'leiter', 'einheitsführer', 'geräteprüfer', 'höhenretter']);

function normalizeRoles(user) {
  const roles = Array.isArray(user?.authorizationRoles) ? user.authorizationRoles : user?.roles;
  if (!Array.isArray(roles)) {
    return [];
  }

  return roles
    .filter((role) => typeof role === 'string')
    .map((role) => role.trim())
    .filter(Boolean);
}

export function hasExternalPartnerRole(user) {
  return normalizeRoles(user).includes('extern_partner');
}

export function hasOnlyExternalPartnerRole(user) {
  const roles = normalizeRoles(user);
  return roles.length === 1 && roles.includes('extern_partner');
}

export function hasOnlyDataExchangeReaderRole(user) {
  const roles = normalizeRoles(user);
  return roles.length === 1 && roles.includes(DATA_EXCHANGE_READER_ROLE);
}

export function canManageDataExchange(user) {
  return normalizeRoles(user).some((role) => (
    DATA_EXCHANGE_MANAGER_ROLES.has(role)
    || role.startsWith('lehrhöhenretter')
    || role.startsWith('lehrhoehenretter')
    || role.startsWith('lehrhohenretter')
  ));
}

export function canViewDataExchange(user) {
  return normalizeRoles(user).includes(DATA_EXCHANGE_READER_ROLE) || canManageDataExchange(user);
}

function isSelfReflectionInstructorRole(role) {
  return role.startsWith('lehrhöhenretter')
    || role.startsWith('lehrhoehenretter')
    || role.startsWith('lehrhohenretter');
}

export function canUseSelfReflection(user) {
  const roles = normalizeRoles(user);
  return roles.some((role) => (
    ['admin', 'leiter', 'einheitsführer', 'höhenretter'].includes(role)
    || isSelfReflectionInstructorRole(role)
  ));
}

export function canAssessSelfReflection(user) {
  return normalizeRoles(user).some((role) => (
    role === 'admin' || role === 'leiter' || isSelfReflectionInstructorRole(role)
  ));
}

export function canAnalyzeSelfReflection(user) {
  return normalizeRoles(user).some((role) => (
    role === 'admin' || role === 'leiter' || role === 'einheitsführer' || isSelfReflectionInstructorRole(role)
  ));
}

export function getAuthenticatedHomePath(user) {
  return hasOnlyDataExchangeReaderRole(user) ? '/einsatz/datenaustausch' : '/dashboard';
}

function isArsExceptionUser(user) {
  const userId = Number.parseInt(String(user?.id || '').trim(), 10);
  if (Number.isInteger(userId) && ARS_EXCEPTION_USER_IDS.has(userId)) {
    return true;
  }

  const userFullName = String(user?.fullName || user?.full_name || '').trim();
  return Boolean(userFullName && userFullName === 'Kehnen, Felix');
}

function isAppSupportExceptionUser(user) {
  const userId = Number.parseInt(String(user?.id || '').trim(), 10);
  if (Number.isInteger(userId) && APP_SUPPORT_EXCEPTION_USER_IDS.has(userId)) {
    return true;
  }

  const userFullName = String(user?.fullName || user?.full_name || '').trim();
  return Boolean(userFullName && userFullName === 'Kehnen, Felix');
}

function canAccessArsFeedback(user) {
  const roles = normalizeRoles(user);
  return roles.includes('admin') || isArsExceptionUser(user);
}

function canAccessAppSupportEvaluation(user) {
  const roles = normalizeRoles(user);
  return roles.includes('admin') || isAppSupportExceptionUser(user);
}

export function hasAssessmentCandidateRole(user) {
  const roles = normalizeRoles(user);
  return roles.includes('bewerber_srht') || roles.includes('bewerber_srht_extern');
}

function hasCandidateOnlyAccess(user) {
  const roles = normalizeRoles(user);
  return roles.length === 1 && hasAssessmentCandidateRole(user);
}

export function isAssessmentObserver(user) {
  return normalizeRoles(user).includes('beobachter');
}

function hasObserverOnlyAccess(user) {
  const roles = normalizeRoles(user);
  return roles.length === 1 && roles.includes('beobachter');
}

function hasAssessmentOnlyAccess(user) {
  return hasCandidateOnlyAccess(user) || hasObserverOnlyAccess(user);
}

export function getHighestRoleLevel(user) {
  const roles = normalizeRoles(user);

  if (
    roles.includes('admin')
    || roles.includes('leiter')
    || roles.includes('lehrhohenretter_ausbildung')
    || roles.includes('lehrhöhenretter_ausbildung')
    || roles.includes('lehrhoehenretter_ausbildung')
  ) {
    return ROLE_LEVELS.ADVANCED;
  }

  if (roles.some((role) => role.startsWith('lehrhohenretter') || role.startsWith('lehrhöhenretter') || role.startsWith('lehrhoehenretter'))) {
    return ROLE_LEVELS.INSTRUCTOR;
  }

  return ROLE_LEVELS.BASE;
}

export function canAccessInstructorFeatures(user) {
  const level = getHighestRoleLevel(user);
  return level === ROLE_LEVELS.INSTRUCTOR || level === ROLE_LEVELS.ADVANCED;
}

export function canAccessStatistics(user) {
  return canAccessInstructorFeatures(user);
}

export function canAccessAdvancedFeatures(user) {
  return getHighestRoleLevel(user) === ROLE_LEVELS.ADVANCED;
}

export function canManageActivityTypes(user) {
  const roles = normalizeRoles(user);
  return (
    roles.includes('admin')
    || roles.includes('leiter')
    || roles.includes('lehrhohenretter_ausbildung')
    || roles.includes('lehrhöhenretter_ausbildung')
    || roles.includes('lehrhoehenretter_ausbildung')
  );
}

export function canManageStatisticsForeignEntries(user) {
  const roles = normalizeRoles(user);
  return roles.includes('admin')
    || roles.includes('leiter')
    || roles.includes('lehrhöhenretter_ausbildung');
}

export function canAccessAssessmentModule(user) {
  return canAccessInstructorFeatures(user) || hasAssessmentCandidateRole(user) || isAssessmentObserver(user);
}

export function canAccessSrhtCourseModule(user) {
  return canAccessInstructorFeatures(user) || hasAssessmentCandidateRole(user) || canManageSrhtChecklist(user);
}

export function canAccessSrhtExamModule(user) {
  return canAccessInstructorFeatures(user) || hasAssessmentCandidateRole(user) || isAssessmentObserver(user);
}

export function canManageSrhtExamEvents(user) {
  const roles = normalizeRoles(user);
  return (
    roles.includes('admin')
    || roles.includes('leiter')
    || roles.includes('lehrhohenretter_ausbildung')
    || roles.includes('lehrhöhenretter_ausbildung')
    || roles.includes('lehrhoehenretter_ausbildung')
  );
}

export function canManageSrhtChecklist(user) {
  const roles = normalizeRoles(user);
  return (
    roles.includes('leiter')
    || roles.includes('lehrhohenretter_ausbildung')
    || roles.includes('lehrhöhenretter_ausbildung')
    || roles.includes('lehrhoehenretter_ausbildung')
  );
}

export function canViewSrhtExamWrittenResults(user) {
  return canAccessInstructorFeatures(user);
}

export function canEditSrhtExamWrittenResults(user) {
  return canManageSrhtExamEvents(user);
}

export function canEditSrhtExamPracticalSheets(user) {
  return canAccessInstructorFeatures(user);
}

export function canAccessEinsatzFeatures(user) {
  if (hasOnlyExternalPartnerRole(user) || hasOnlyDataExchangeReaderRole(user)) {
    return false;
  }
  return !hasAssessmentOnlyAccess(user);
}

export function canAccessRueckmeldungenFeatures(user) {
    if (hasOnlyDataExchangeReaderRole(user)) {
      return false;
    }
  if (hasOnlyExternalPartnerRole(user)) {
    return canAccessArsFeedback(user);
  }
  return !hasAssessmentOnlyAccess(user);
}

export function canAccessLogbuchFeatures(user) {
  if (hasOnlyExternalPartnerRole(user) || hasOnlyDataExchangeReaderRole(user)) {
    return false;
  }
  return !hasAssessmentOnlyAccess(user);
}

export function canCreateLogbuchEntries(user) {
  if (hasOnlyExternalPartnerRole(user) || hasOnlyDataExchangeReaderRole(user)) {
    return false;
  }

  if (hasAssessmentOnlyAccess(user)) {
    return false;
  }

  const roles = normalizeRoles(user);
  return roles.includes('admin')
    || roles.includes('höhenretter')
    || roles.includes('einheitsführer')
    || roles.some((role) => role.startsWith('lehrhohenretter') || role.startsWith('lehrhöhenretter'));
}

export function canAccessUebungsplanungFeatures(user) {
  return canAccessInstructorFeatures(user);
}

export function canEditAssessments(user) {
  return canAccessInstructorFeatures(user);
}

export function canViewAssessmentLiveResults(user) {
  return canAccessInstructorFeatures(user) || isAssessmentObserver(user);
}

export function canManageAssessmentEvents(user) {
  const roles = normalizeRoles(user);
  return (
    roles.includes('admin')
    || roles.includes('leiter')
    || roles.includes('lehrhohenretter_ausbildung')
    || roles.includes('lehrhöhenretter_ausbildung')
  );
}

export function getMenuPermissions(user) {
    if (hasOnlyDataExchangeReaderRole(user)) {
      return {
        showEinsatz: true,
        showLogbuch: false,
        showRueckmeldungen: false,
        showPasswortAendern: false,
        showBenachrichtigungen: false,
        showBenutzer: false,
        showTaetigkeiten: false,
        showAuditLog: false,
        showEignungsverfahren: false,
        showLehrgangSrht: false,
        showPruefungSrht: false,
        logbuchEintragVornehmen: false,
        logbuchAnsicht: false,
        logbuchStatistik: false,
        logbuchUebungsplanung: false,
      };
    }

  if (hasOnlyExternalPartnerRole(user)) {
    return {
      showEinsatz: false,
      showLogbuch: false,
      showRueckmeldungen: canAccessArsFeedback(user),
      showPasswortAendern: true,
      showBenachrichtigungen: true,
      showBenutzer: false,
      showTaetigkeiten: false,
      showAuditLog: false,
      showEignungsverfahren: false,
      showLehrgangSrht: false,
      showPruefungSrht: false,
      logbuchEintragVornehmen: false,
      logbuchAnsicht: false,
      logbuchStatistik: false,
      logbuchUebungsplanung: false,
    };
  }

  const instructor = canAccessInstructorFeatures(user);
  const advanced = canAccessAdvancedFeatures(user);
  const manageActivityTypes = canManageActivityTypes(user);
  const assessmentOnly = hasAssessmentOnlyAccess(user);
  const canCreateLogbuch = canCreateLogbuchEntries(user);

  return {
    showEinsatz: !assessmentOnly,
    showLogbuch: !assessmentOnly,
    showRueckmeldungen: !assessmentOnly,
    showPasswortAendern: true,
    showBenachrichtigungen: true,
    showBenutzer: advanced,
    showTaetigkeiten: manageActivityTypes,
    showAuditLog: advanced,
    showEignungsverfahren: canAccessAssessmentModule(user),
    showLehrgangSrht: canAccessSrhtCourseModule(user),
    showPruefungSrht: canAccessSrhtExamModule(user),
    logbuchEintragVornehmen: canCreateLogbuch,
    logbuchAnsicht: !assessmentOnly,
    logbuchStatistik: canAccessStatistics(user),
    logbuchUebungsplanung: instructor,
  };
}

export function getDashboardPermissions(user) {
    if (hasOnlyDataExchangeReaderRole(user)) {
      return {
        showLogbuch: false,
        logbuchEintragVornehmen: false,
        logbuchAnsicht: false,
        showEinsatz: true,
        showRueckmeldungen: false,
        showEignungsverfahren: false,
        showLehrgangSrht: false,
        showPruefungSrht: false,
        showUebungsplanung: false,
        showBenutzer: false,
        showTaetigkeiten: false,
        showAuditLog: false,
        showAdministrationOnly: false,
        showDashboardGreetingOnly: false,
      };
    }

  if (hasOnlyExternalPartnerRole(user)) {
    const allowRueckmeldungen = canAccessArsFeedback(user);

    return {
      showLogbuch: false,
      logbuchEintragVornehmen: false,
      logbuchAnsicht: false,
      showEinsatz: false,
      showRueckmeldungen: allowRueckmeldungen,
      showEignungsverfahren: false,
      showLehrgangSrht: false,
      showPruefungSrht: false,
      showUebungsplanung: false,
      showBenutzer: false,
      showTaetigkeiten: false,
      showAuditLog: false,
      showAdministrationOnly: !allowRueckmeldungen,
      showDashboardGreetingOnly: true,
    };
  }

  const instructor = canAccessInstructorFeatures(user);
  const advanced = canAccessAdvancedFeatures(user);
  const manageActivityTypes = canManageActivityTypes(user);
  const assessmentOnly = hasAssessmentOnlyAccess(user);
  const canCreateLogbuch = canCreateLogbuchEntries(user);

  return {
    showLogbuch: !assessmentOnly,
    logbuchEintragVornehmen: canCreateLogbuch,
    logbuchAnsicht: !assessmentOnly,
    showEinsatz: !assessmentOnly,
    showRueckmeldungen: !assessmentOnly,
    showEignungsverfahren: canAccessAssessmentModule(user),
    showLehrgangSrht: canAccessSrhtCourseModule(user),
    showPruefungSrht: canAccessSrhtExamModule(user),
    showUebungsplanung: instructor,
    showBenutzer: advanced,
    showTaetigkeiten: manageActivityTypes,
    showAuditLog: advanced,
    showAdministrationOnly: false,
    showDashboardGreetingOnly: false,
  };
}

export function getAdministrationMenuItems(user) {
  const permissions = getMenuPermissions(user);
  const items = [
    permissions.showPasswortAendern
      ? { label: 'Passwort ändern', path: '/passwort-aendern' }
      : null,
    permissions.showBenachrichtigungen
      ? { label: 'Benachrichtigungen', path: '/benachrichtigungen' }
      : null,
    permissions.showBenutzer
      ? { label: 'Benutzer', path: '/benutzer' }
      : null,
    permissions.showTaetigkeiten
      ? { label: 'Arten von Tätigkeiten', path: '/taetigkeiten' }
      : null,
    permissions.showAuditLog
      ? { label: 'Audit Log', path: '/audit' }
      : null,
  ];

  return items.filter(Boolean);
}

export function getGrundlehrgangSrhtMenuItems(user) {
  const permissions = getMenuPermissions(user);
  if (!permissions.showEignungsverfahren && !permissions.showLehrgangSrht && !permissions.showPruefungSrht && !canManageSrhtChecklist(user)) {
    return [];
  }

  const items = [];

  if (permissions.showEignungsverfahren) {
    items.push({ label: 'Eignungsverfahren', path: '/eignungsverfahren' });
  }

  if (permissions.showLehrgangSrht) {
    items.push({ label: 'Lehrgang SRHT', path: '/lehrgang-srht' });
  }

  if (permissions.showPruefungSrht) {
    items.push({ label: 'Prüfung SRHT', path: '/pruefung-srht' });
  }

  if (canManageSrhtChecklist(user)) {
    items.push({ label: 'Laufzettel', path: '/laufzettel-srht' });
  }

  return items;
}

export function getRueckmeldungenMenuItems(user) {
  if (!canAccessRueckmeldungenFeatures(user)) {
    return [];
  }

  if (hasOnlyExternalPartnerRole(user)) {
    return canAccessArsFeedback(user)
      ? [{ label: 'Windenrettung', path: '/rueckmeldung?type=ars' }]
      : [];
  }

  const items = [
    { label: 'Zwischenfälle', path: '/rueckmeldung?type=zwischenfaelle' },
  ];

  if (canUseSelfReflection(user)) {
    items.push({ label: 'Selbstreflexion', path: '/rueckmeldung/selbstreflexion' });
  }

  items.push(
    { label: 'Disposition HöRett', path: '/rueckmeldung?type=disposition' },
    { label: 'App-Support', path: '/rueckmeldung?type=app-support' },
  );

  if (canAccessArsFeedback(user)) {
    items.push({ label: 'Windenrettung', path: '/rueckmeldung?type=ars' });
  }

  return items;
}

export function getEinsatzMenuItems(user) {
  if (hasOnlyDataExchangeReaderRole(user)) {
    return [{ label: 'Datenaustausch', path: '/einsatz/datenaustausch' }];
  }

  const items = [
    { label: 'PeRM', path: '/einsatz?type=perm' },
    { label: 'Einseilrettung', path: '/einsatz?type=einseilrettung' },
  ];
  if (canViewDataExchange(user)) {
    items.push({ label: 'Datenaustausch', path: '/einsatz/datenaustausch' });
  }
  return items;
}

export function canEvaluateAppSupport(user) {
  return canAccessAppSupportEvaluation(user);
}

export function getRoleDisplayPriority(user) {
  const level = getHighestRoleLevel(user);
  if (level === ROLE_LEVELS.ADVANCED) return 'advanced';
  if (level === ROLE_LEVELS.INSTRUCTOR) return 'instructor';
  return 'base';
}
